import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Separator } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Save, Copy, Download, AlertTriangle } from 'lucide-react'
import type { CommunityResult } from '@creanizer/types'
import { CommunityOverview } from './CommunityOverview'
import { CommunityFeatures } from './CommunityFeatures'
import { CommunityResidents } from './CommunityResidents'
import { CommunityHistory } from './CommunityHistory'
import { CommunityAnecdotes } from './CommunityAnecdotes'

interface CommunityDisplayProps {
  result: CommunityResult
  onSave?: () => void
  isSaved?: boolean
}

export function CommunityDisplay({ result, onSave, isSaved }: CommunityDisplayProps) {
  const [copiedTab, setCopiedTab] = useState<string | null>(null)
  
  // データ完全性チェック
  const checkDataCompleteness = () => {
    const missingFields: string[] = []
    if (!result.basicInfo) missingFields.push('基本情報')
    if (!result.features) missingFields.push('特徴')
    if (!result.residents) missingFields.push('住民')
    if (!result.history) missingFields.push('歴史')
    if (!result.anecdotes) missingFields.push('逸話')
    if (!result.anecdotes?.anecdotes || result.anecdotes.anecdotes.length === 0) {
      if (!missingFields.includes('逸話')) missingFields.push('逸話詳細')
    }
    return missingFields
  }
  
  const missingFields = checkDataCompleteness()

  const copyToClipboard = (text: string, tabName: string) => {
    navigator.clipboard.writeText(text)
    setCopiedTab(tabName)
    setTimeout(() => setCopiedTab(null), 2000)
  }

  const exportAsText = () => {
    const lines: string[] = []
    
    // ヘッダー
    lines.push(`【コミュニティ】${result.basicInfo.name}`)
    lines.push('━'.repeat(50))
    lines.push('')
    
    // 基本情報
    lines.push('■ 基本情報')
    lines.push(`名前: ${result.basicInfo.name}`)
    if (result.basicInfo.fullName) {
      lines.push(`正式名称: ${result.basicInfo.fullName}`)
    }
    lines.push(`タイプ: ${result.basicInfo.type}`)
    lines.push(`規模: ${result.basicInfo.scale}`)
    lines.push(`人口: ${result.basicInfo.population.toLocaleString()}人`)
    if (result.basicInfo.foundedYear) {
      lines.push(`創立年: ${result.basicInfo.foundedYear}`)
    }
    if (result.basicInfo.motto) {
      lines.push(`モットー: ${result.basicInfo.motto}`)
    }
    lines.push(`雰囲気: ${result.basicInfo.atmosphere}`)
    lines.push(`第一印象: ${result.basicInfo.firstImpression}`)
    lines.push('')
    
    // 特徴
    lines.push('■ 特徴')
    lines.push('◆ 地理的特徴')
    lines.push(result.features.geography)
    lines.push('')
    lines.push('◆ 主要施設')
    lines.push(result.features.facilities)
    lines.push('')
    lines.push('◆ 文化')
    lines.push(result.features.culture)
    lines.push('')
    lines.push('◆ 問題')
    lines.push(result.features.problems)
    lines.push('')
    lines.push('◆ 政治')
    lines.push(result.features.politics)
    lines.push('')
    lines.push('◆ 経済')
    lines.push(result.features.economy)
    lines.push('')
    
    // 住民
    lines.push('■ 住民')
    lines.push(`総人口: ${result.residents.totalPopulation.toLocaleString()}人`)
    lines.push(result.residents.characteristics)
    lines.push('')
    
    // 有名人
    if (result.residents.notableResidents?.length) {
      lines.push('■ 有名人')
      result.residents.notableResidents.forEach(resident => {
        lines.push(`◆ ${resident.name}（${resident.occupation}）`)
        lines.push(`  ${resident.description}`)
      })
      lines.push('')
    }
    
    // 歴史
    lines.push('■ 歴史')
    lines.push('◆ 創立物語')
    lines.push(result.history.foundingStory)
    lines.push('')
    lines.push('◆ 年表')
    result.history.timeline.forEach(event => {
      lines.push(`${event.year}: ${event.event}`)
    })
    lines.push('')
    
    // 逸話
    if (result.anecdotes?.anecdotes?.length) {
      lines.push('■ 逸話')
      result.anecdotes.anecdotes.forEach((anecdote, index) => {
        lines.push(`◆ ${anecdote.title}（${anecdote.category}）`)
        lines.push(`  出典: ${anecdote.source} / 信憑性: ${anecdote.reliability}`)
        lines.push(`  ${anecdote.content}`)
        if (index < result.anecdotes.anecdotes.length - 1) lines.push('')
      })
    }
    
    const text = lines.join('\n')
    
    // ダウンロード
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `community_${result.basicInfo.name}_${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold">{result.basicInfo.name}</h4>
          {result.basicInfo.fullName && result.basicInfo.fullName !== result.basicInfo.name && (
            <p className="text-sm text-gray-500">{result.basicInfo.fullName}</p>
          )}
        </div>
        <div className="flex gap-2">
          {onSave && (
            <Button
              size="sm"
              onClick={onSave}
              disabled={isSaved}
              variant={isSaved ? 'secondary' : 'default'}
            >
              <Save className="h-4 w-4 mr-1" />
              {isSaved ? '保存済み' : '保存'}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={exportAsText}>
            <Download className="h-4 w-4 mr-1" />
            テキスト出力
          </Button>
        </div>
      </div>

      {/* データ不完全の警告 */}
      {missingFields.length > 0 && (
        <Alert className="border-yellow-500">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            一部のデータが不完全です: {missingFields.join(', ')}
            <br />
            <span className="text-xs text-muted-foreground">
              表示可能な内容のみ表示しています。生成時にエラーが発生した可能性があります。
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* 基本情報バッジ */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="secondary">{result.basicInfo?.type || '不明'}</Badge>
        <Badge variant="secondary">規模: {result.basicInfo?.scale || '不明'}</Badge>
        <Badge variant="secondary">人口: {result.basicInfo?.population?.toLocaleString() || '不明'}</Badge>
        {result.basicInfo?.foundedYear && (
          <Badge variant="secondary">創立: {result.basicInfo.foundedYear}</Badge>
        )}
      </div>

      <Separator />

      {/* タブ表示 */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid grid-cols-5 h-auto">
          <TabsTrigger value="overview" className="text-xs">概要</TabsTrigger>
          <TabsTrigger value="features" className="text-xs">特徴</TabsTrigger>
          <TabsTrigger value="residents" className="text-xs">住民</TabsTrigger>
          <TabsTrigger value="history" className="text-xs">歴史</TabsTrigger>
          <TabsTrigger value="anecdotes" className="text-xs">逸話</TabsTrigger>
        </TabsList>

        <ScrollArea className="h-[600px] mt-4">
          <TabsContent value="overview">
            <CommunityOverview result={result} onCopy={(text) => copyToClipboard(text, 'overview')} />
          </TabsContent>

          <TabsContent value="features">
            <CommunityFeatures features={result.features} onCopy={(text) => copyToClipboard(text, 'features')} />
          </TabsContent>

          <TabsContent value="residents">
            <CommunityResidents residents={result.residents} onCopy={(text) => copyToClipboard(text, 'residents')} />
          </TabsContent>

          <TabsContent value="history">
            <CommunityHistory history={result.history} onCopy={(text) => copyToClipboard(text, 'history')} />
          </TabsContent>

          <TabsContent value="anecdotes">
            {result.anecdotes && (
              <CommunityAnecdotes anecdotes={result.anecdotes} onCopy={(text) => copyToClipboard(text, 'anecdotes')} />
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>

      {/* コピー完了通知 */}
      {copiedTab && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg">
          コピーしました！
        </div>
      )}
    </div>
  )
}